import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	AbstractModal,
	BroadcastService,
	ModalButtonModel,
	ModalButtonType,
	ObjectUtils,
	ToasterType
} from '@cv-invision/core';
import { LenderService } from '../../service/lender.service';
import { AcuityLenderBranch, Lender, LenderMapper, LenderModel } from '../model/lender.model';
import { GlobalConfigurationState } from '../../state/global-configuration.state';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'cv-invision-create-update-lender',
	templateUrl: './create-update-lender.component.html',
	styleUrls: ['./create-update-lender.component.scss']
})
export class CreateUpdateLenderComponent extends AbstractModal implements OnInit, OnDestroy {
	private atComponentDestroy: Subject<any> = new Subject<any>();
	lenderMapperData: LenderMapper;
	oldLenderMapperData: LenderMapper;
	acuityLenderBranchesList: any;
	cmpAcuityLenderBranchesList: any;
	saving: boolean;
	lendersList: any;

	acuityAppScopeBranchSelected: Array<AcuityLenderBranch>;
	cmpAcuityAppScopeBranchSelected: Array<AcuityLenderBranch>;
	lenderSelected: Array<Lender>;
	lenderConfiguration: LenderModel;
	filterLenders = {
		title: '',
		pagination: {
			totalCount: 0,
			offset: 0,
			limit: 10
		}
	};

	constructor(
		private store: Store,
		public lenderService: LenderService,
		private broadcastService: BroadcastService
	) {
		super();
		this.lenderMapperData = new LenderMapper();
		this.saving = false;
		this.acuityAppScopeBranchSelected = [];
		this.cmpAcuityAppScopeBranchSelected = [];
		this.lenderSelected = [];
	}

	ngOnInit(): void {
		this.acuityLenderBranchesList = this.store.selectSnapshot(GlobalConfigurationState.getAcuityLenderBranches);
		this.cmpAcuityLenderBranchesList = this.store.selectSnapshot(GlobalConfigurationState.getCmpAcuityLenderBranches);
		this.lendersList = this.store.selectSnapshot(GlobalConfigurationState.getLenders);

		this.lenderService
			.getLenderConfiguration(this.filterLenders)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(
				result => {
					if (result) {
						this.lenderConfiguration = result;
					}
				},
				error => console.error(error)
			);

		if (this.data.isEditing) {
			this.lenderMapperData = { ...this.data.lenderMapperData };
			this.oldLenderMapperData = ObjectUtils.recursiveDeepCopy(this.data.lenderMapperData);

			if(this.oldLenderMapperData.insideMapsOrgId === null ){
				this.oldLenderMapperData.insideMapsOrgId = "";
			}

			if(this.lenderMapperData.insideMapsOrgId === null ){
				this.lenderMapperData.insideMapsOrgId = "";
			}

			this.acuityAppScopeBranchSelected = [{
				id: this.data.lenderMapperData.acuityId,
				title: this.data.lenderMapperData.acuityName
			}];

			if (this.data.lenderMapperData.scopeId && this.data.lenderMapperData.scopeName) {
				this.lenderSelected = [{
					id: this.data.lenderMapperData.scopeId,
					title: this.data.lenderMapperData.scopeName
				}];
			}
		}

		const cancelButton: ModalButtonModel = {
			name: 'close',
			text: 'Close',
			class: 'btn-sm',
			type: ModalButtonType.ACTION,
			action: this.onCancel.bind(this)
		};
		const confirmButton: ModalButtonModel = {
			name: 'save',
			text: 'Save',
			class: 'btn-sm cv-btn-blue ',
			type: ModalButtonType.ACTION,
			disabled: (): boolean => !this.setIsFormValid() || this.saving,
			action: () => this.onSave(),
			loading: (): boolean => this.saving,
			loadingText: 'Saving'
		};

		this.buttons.push(cancelButton);
		this.buttons.push(confirmButton);
	}

	/**
	 * On Save button.
	 */
	onSave(): void {
		if (this.data.isEditing) {
			this.updateLender(this.lenderMapperData, this.oldLenderMapperData);
		} else {
			this.createLender(this.lenderMapperData);
		}
	}

	createLender(lenderMapper: LenderMapper): void{
		this.lenderService.createLender(lenderMapper).subscribe((response : any) => {
			if (response.succeeded) {
				this.successHandleFunction();
				super.onAcceptSuccess({
					success: true
				});
			}
		})
	}

	updateLender(newlenderMapper: LenderMapper, oldlenderMapper: LenderMapper): void{
		this.lenderService.updateLender(newlenderMapper, oldlenderMapper).subscribe((response : any) => {
			if (response.succeeded) {
				this.successHandleFunction();
				super.onAcceptSuccess({
					success: true
				});
			}
		})
	}

	/**
	 * Select assign to
	 * @param $event
	 */
	onAcuityAppScopeBranchSelected($event: any): void {
		this.lenderMapperData.acuityId = $event ? $event.id : '';
		this.lenderMapperData.acuityName = $event ? $event.title : '';
	}

	/**
	 * Select assign to
	 * @param $event
	 */
	onCmpAcuityBranchSelected($event: any): void {
		this.lenderMapperData.cmpAcuityId = $event ? $event.id : '';
		this.lenderMapperData.cmpAcuityName = $event ? $event.title : '';
	}

	/**
	 * Select Access Level
	 * @param $event
	 */
	onLenderSelected($event: any): void {
		this.lenderMapperData.scopeId = $event ? $event.id : '';
		this.lenderMapperData.scopeName = $event ? $event.title : '';
	}

	/**
	 * handle response from server
	 */
	successHandleFunction(): void {
		const msgSuget = this.data.isEditing ? 'Updated' : 'Created';
		const msg = `The Mapper Lender Configuration Was ${msgSuget} Successfully`;
		this.broadcastService.broadcast({
			name: ToasterType.SUCCESS,
			event: { message: msg }
		});
	}

	/**
	 * Display error message if the operation was not success
	 * @param error
	 */
	errorMsgFunction(error: string): void {
		this.broadcastService.broadcast({
			name: ToasterType.ERROR,
			event: { error }
		});
	}

	/**
	 * Is form valid?
	 */
	setIsFormValid(): boolean {
		if (this.lenderMapperData.acuityId) {
			return true;
		} else {
			return false;
		}
	}

	onDismiss(): void {
		super.onCancel();
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
